import { LocationIcon } from '@/components/icons/location-icon';
import { GeoLocationComponent } from '@/components/location/geo-location/geo-location';
import { ZipLocationComponent } from '@/components/location/zip-code-location/zip-code-location';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useSharedState } from '@/hooks/state/shared-state';
import { useTheaterContext } from '@/hooks/state/theater';
import { useAnimatedDropdown } from '@/hooks/use-animated-dropdown';
import clsx from 'clsx';
import { usePathname } from 'next/navigation';
import userLocationStyles from './user-location.module.scss';
import { ZipSearchForm } from './zip-search-form';

export const UserLocation = (): React.JSX.Element => {
  const { close, containerRef, isClosing, isCollapsed, toggle } = useAnimatedDropdown({ closeOnOutsideClick: true });
  const pathname = usePathname();
  const { data: theater, isLoading: loadingTheater } = useTheaterContext();
  const { data: sharedState, isLoading: loadingSharedState } = useSharedState();
  const isMoviesPage = pathname.startsWith('/movies');
  const locationText = isMoviesPage
    ? sharedState?.lastTextSearch?.toString() || theater?.zipcode
    : theater?.title || sharedState?.lastTextSearch?.toString();
  const showSkeleton = loadingTheater || loadingSharedState;

  return (
    <div ref={containerRef}
      className="flex items-start gap-2"
    >
      {showSkeleton &&
          <div className={clsx('flex items-center gap-sm')}
            role="status"
          >
            <span className="sr-only">Loading theater/zipcode location...</span>
            <Skeleton className="rounded-md w-3.5 h-3.5 md:w-5 md:h-5" />
            <Skeleton className="h-3 w-30 md:h-4" />
          </div>
      }

      {(theater || sharedState?.lastTextSearch) && !showSkeleton &&
        <Button className="flex shrink-0 items-end gap-2 size-auto font-normal group"
          data-test="button-toggle-user-location"
          size="icon"
          variant="ghost"
          onClick={toggle}
        >
          <LocationIcon aria-hidden="true"
            className={clsx('icon-primary dont-grow group-hover:!fill-primary-darker', userLocationStyles.icon, userLocationStyles['mini-icon'])}
            data-test="user-location-location-icon"
            viewBox="0 0 20 20"
          />
          <span className="text-xs max-w-32 md:max-w-96 lg:max-w-64 inline-block truncate"
            data-test="user-location-theater-title"
            title={locationText}
          >
            {locationText}
          </span>
          <span className="text-primary font-bold text-xs"
            data-test="user-location-theater-change"
          >
            change
          </span>
        </Button>
      }

      {!theater && !sharedState?.lastTextSearch && !showSkeleton &&
        <>
          <GeoLocationComponent mini
            className="has-[>svg]:p-0"
            miniText="Location"
          />
          <ZipLocationComponent className="has-[>svg]:p-0"
            miniText2="ZIP Search"
            type="mini"
            onMiniClick={toggle}
          />
        </>
      }

      {!isCollapsed && <ZipSearchForm close={close}
        isClosing={isClosing}
      />}
    </div>
  );
};
